import { useState } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';

const cardStyle = {
  backgroundColor: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
};

// RemoveVideoDialog — moderator takedown of an approved video (045).
//
// Its own component rather than JSX inside VideoScreen for one concrete reason:
// useModalDismiss is a hook, so it cannot be called conditionally, and calling
// it in VideoScreen would lock page scroll whenever that screen is mounted.
// Every modal in this app handles Escape and locks scroll behind it — see the
// hook's own notes on why (including the pull-to-refresh interaction).
//
// A deliberate two-step with a reason field: this removes someone's video from
// the library and notifies them, and the reason is the only place the decision
// gets written down.
const RemoveVideoDialog = ({ isUpload, onConfirm, onClose }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useModalDismiss(() => { if (!busy) onClose(); });

  const submit = async () => {
    setBusy(true);
    setError('');
    const message = await onConfirm(reason.trim() || null);
    // onConfirm resolves with an error string, or null on success (in which
    // case the caller navigates away and this unmounts).
    if (message) {
      setError(message);
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={() => !busy && onClose()}
      role="dialog"
      aria-modal="true"
      aria-label="Remove this video"
    >
      <div
        className="w-full max-w-md rounded-2xl p-5"
        style={cardStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-bold mb-1 text-primary">Remove this video?</h3>
        <p className="text-sm text-secondary leading-relaxed mb-3">
          It will be taken out of the library and the person who submitted it will be
          notified.{isUpload ? ' The uploaded file will be deleted.' : ''} This cannot be
          undone from here.
        </p>

        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 300))}
          placeholder="Reason (optional, kept on the record)"
          className="w-full px-3 py-2 rounded-lg text-sm outline-none mb-3"
          style={{
            backgroundColor: 'var(--input-bg)',
            border: '1px solid var(--card-border)',
            color: 'var(--text-primary)',
          }}
        />

        {error && <p className="text-xs mb-3" style={{ color: '#EF4444' }}>{error}</p>}

        <div className="flex gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all hover-surface active:scale-[0.98] disabled:opacity-50"
            style={{ border: '1px solid var(--card-border)', color: 'var(--text-secondary)' }}
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50"
            style={{ backgroundColor: '#EF4444' }}
          >
            {busy ? 'Removing…' : 'Remove video'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RemoveVideoDialog;
